// Shared building blocks for the timeline renderers: luxon time axis, zero-filling,
// and the axes-only empty state.
import { Chart } from 'chart.js'
import 'chartjs-adapter-luxon'

import { cssVar } from './palette'

// Presenter colors arrive as `var(--chart-*)`; Chart.js needs a resolved color for fills.
export function resolveColor(value) {
    if (typeof value !== 'string') return value
    const match = value.match(/^var\((--[\w-]+)\)$/)
    return match ? cssVar(match[1], value) : value
}

// Major ticks are always exactly one unit above the tick unit (minute→hour, hour→day),
// so any window that can cross midnight must tick in hours — that is what makes the
// day boundary surface as a "Sat 21" major instead of an anonymous "12 AM".
export function tickUnitFor(intervalMs) {
    if (intervalMs < 60 * 1000) return 'second' // 1s–30s buckets (≤ 30m windows)
    if (intervalMs < 20 * 60 * 1000) return 'minute' // 1m–2m buckets (≤ 4h windows)
    if (intervalMs < 24 * 60 * 60 * 1000) return 'hour' // 20m–12h buckets (4h–1mo windows)
    if (intervalMs < 7 * 24 * 60 * 60 * 1000) return 'day' // 1d buckets (1–6mo windows)
    return 'week' // 1w buckets (1y+ windows)
}

// Zero-fill the buckets the server omitted, so bars stay evenly spaced
export function fillGaps(points, intervalMs, windowStartMs, windowEndMs) {
    // # NOTE: If DrillDownQueryBuilder returns gap filled data, this method can be removed. And we will rely on the
    // server to do this. If you want to do this in the Mobile or MPC. PLEASE implement gap filling in DrillDown and do
    // not copy over this code.
    // return points
    if (!points.length || !intervalMs) return points

    const filled = []
    const pushZerosBetween = (fromX, beforeX) => {
        for (let x = fromX; x < beforeX - intervalMs * 0.5; x += intervalMs) {
            filled.push({ x, y: 0 })
        }
    }

    let leadingX = points[0].x
    while (leadingX - intervalMs >= windowStartMs) leadingX -= intervalMs
    pushZerosBetween(leadingX, points[0].x)

    points.forEach((point, i) => {
        filled.push(point)
        const next = points[i + 1]
        if (next) pushZerosBetween(point.x + intervalMs, next.x)
    })

    pushZerosBetween(points[points.length - 1].x + intervalMs, windowEndMs + intervalMs * 0.5)

    return filled
}

// The data-no-data attribute is the hook system specs assert.
export function markNoData(canvas, datasets) {
    const noData = datasets.every((dataset) => !dataset.data || !dataset.data.length)
    canvas.toggleAttribute('data-no-data', noData)
    return noData
}

// Datadog-style empty state: the time axis still renders the selected window, with a
// centered message where the bars would be.
export function noDataPlugin(message, textColor) {
    return {
        id: 'timeAxisNoData',
        afterDraw(chart) {
            const { left, right, top, bottom } = chart.chartArea
            const ctx = chart.ctx
            ctx.save()
            ctx.font = `500 14px ${Chart.defaults.font.family}`
            ctx.fillStyle = textColor
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(message, (left + right) / 2, (top + bottom) / 2)
            ctx.restore()
        },
    }
}

export function timeXScaleOptions(timeScale, { stacked = false, offset = true } = {}) {
    const intervalMs = (timeScale.intervalSeconds || 0) * 1000
    return {
        type: 'time',
        time: {
            unit: tickUnitFor(intervalMs),
            // Luxon tokens. Boundary (major) ticks render with the next-larger
            // unit's format, so the day surfaces exactly where it changes.
            displayFormats: {
                second: 'h:mm:ss a', // "10:03:40 AM"
                minute: 'h:mm a', // "10:03 AM"
                hour: 'h a', // "10 AM"
                day: 'ccc d', // "Sun 22" — the weekday is the story at this zoom
                week: 'MMM d', // "Feb 16"
                month: 'MMM yyyy', // "Feb 2026"
            },
            // Tooltip title, e.g. "Mon, 17 Aug 2026, 11:43 PM". Explicit tokens —
            // luxon's localized presets ('ff') render a 24h clock in many locales.
            // Sub-minute buckets include the seconds.
            tooltipFormat:
                intervalMs < 60 * 1000 ? 'ccc, d MMM yyyy, h:mm:ss a' : 'ccc, d MMM yyyy, h:mm a',
        },
        adapters: { date: { zone: timeScale.zone } },
        min: timeScale.windowStart,
        max: timeScale.windowEnd,
        offset,
        stacked,
        grid: { drawTicks: false },
        ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            major: { enabled: true },
            font: (context) =>
                context.tick && context.tick.major ? { weight: 'bold' } : undefined,
        },
    }
}
